import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { ShareJobDialog, buildDigestText } from "./ShareJobDialog";
import { MatchDialog } from "./MatchDialog";
import { AdminBroadcastCard } from "./AdminBroadcastCard";
import { BalcaoManageCards } from "./BalcaoManageCards";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTA_ESCLARECIMENTO = `📢 *AVISO: corrigimos o acesso às vagas!*
Se você se cadastrou e não conseguiu ver o contato, a falha era nossa — já foi resolvida. ✅
Toque no link da vaga: após entrar, você volta direto pra ela e vê o contato na hora.
`;

const stripPhone = (t) =>
  (t || "")
    .replace(/(\+?55\s?)?(\(?\d{2}\)?[\s.\-]?)?9?\d{4}[\s.\-]?\d{4}/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const buildLaborText = (offers) => {
  const nums = ["1️⃣", "2️⃣", "3️⃣", "4️⃣", "5️⃣", "6️⃣", "7️⃣", "8️⃣", "9️⃣", "🔟"];
  const hoje = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  const lines = [
    `🤝 *HEVEATALENT — PROFISSIONAIS DISPONÍVEIS ${hoje}* 🌳`,
    `💪 ${offers.length} profissiona${offers.length > 1 ? "is" : "l"} procurando serviço AGORA`,
    "",
  ];
  offers.forEach((o, i) => {
    lines.push(`${nums[i] || `▪️ ${i + 1}.`} *${o.title}*`);
    if (o.city) lines.push(`📍 ${o.city}`);
    const clean = stripPhone(o.summary || "");
    if (clean) lines.push(clean.length > 220 ? `${clean.slice(0, 220)}…` : clean);
    lines.push(`👉 https://heveatalent.com.br/oferta/${o.id}`);
    lines.push("");
  });
  lines.push("👇 *PRECISA DE GENTE NO SERINGAL?*");
  lines.push("Entre no link do profissional → veja o contato → fale direto (grátis).");
  lines.push("✅ Grátis | ✅ Sem intermediário");
  lines.push("🔗 Todos os profissionais: heveatalent.com.br/balcao");
  return lines.join("\n");
};

const JobBroadcastPanel = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareJob, setShareJob] = useState(null);
  const [showDigest, setShowDigest] = useState(false);
  const [match, setMatch] = useState(null);
  const [filling, setFilling] = useState(null);
  const [signals, setSignals] = useState([]);
  const [renewing, setRenewing] = useState(null);
  const [selected, setSelected] = useState({});
  const [onlyConfirmed, setOnlyConfirmed] = useState(false);
  const [showPrevia, setShowPrevia] = useState(false);
  const [funil, setFunil] = useState(null);
  const [includeNote, setIncludeNote] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [previa, setPrevia] = useState("");
  const [previaScope, setPreviaScope] = useState("ambos");
  const [laborOffers, setLaborOffers] = useState([]);
  const [laborSelected, setLaborSelected] = useState({});
  const [showLaborPreview, setShowLaborPreview] = useState(false);

  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const previaPrefix = previa.trim() ? `${previa.trim()}\n\n` : "";
  const previaJobs = previaScope === "vagas" || previaScope === "ambos" ? previaPrefix : "";
  const previaLabor = previaScope === "profissionais" || previaScope === "ambos" ? previaPrefix : "";
  const notePrefix = includeNote ? `${NOTA_ESCLARECIMENTO}\n` : "";
  const selectedJobs = jobs.filter((j) => selected[j.id]);
  const jobsBase = selectedJobs.length > 0 ? selectedJobs : jobs;
  const jobsToShare = onlyConfirmed ? jobsBase.filter((j) => j.confirmed_at) : jobsBase;
  const confirmedCount = jobs.filter((j) => j.confirmed_at).length;
  const digestText = jobsToShare.length > 0 ? previaJobs + notePrefix + buildDigestText(jobsToShare, { confirmed: onlyConfirmed }) : "";

  const selectedLabor = laborOffers.filter((o) => laborSelected[o.id]);
  const laborToShare = selectedLabor.length > 0 ? selectedLabor : laborOffers;
  const laborText = laborOffers.length > 0 ? previaLabor + buildLaborText(laborToShare) : "";
  const toggleLaborSelect = (id) => setLaborSelected((s) => ({ ...s, [id]: !s[id] }));

  const copyLabor = async () => {
    try {
      await navigator.clipboard.writeText(laborText);
      toast.success(`Mensagem com ${laborToShare.length} profissional(is) copiada!`);
    } catch {
      toast.error("Não consegui copiar — abra a prévia e selecione o texto manualmente.");
    }
  };

  const notifyMissingRefs = async () => {
    if (!window.confirm("Enviar aviso no sininho para TODOS os profissionais sem referência? (não repete para quem já recebeu nos últimos 30 dias)")) return;
    try {
      const res = await axios.post(`${API}/admin/notify-missing-references`);
      toast.success(`Aviso enviado para ${res.data.sent} profissional(is)! ${res.data.skipped_recent > 0 ? `(${res.data.skipped_recent} já avisados recentemente)` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar avisos");
    }
  };

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const copyDigest = async () => {
    try {
      await navigator.clipboard.writeText(digestText);
      toast.success(`Mensagem com ${jobsToShare.length} vaga(s) copiada! É só colar no grupo.`);
    } catch {
      toast.error("Não consegui copiar — abra a prévia e selecione o texto manualmente.");
    }
  };

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/vagas-divulgacao`);
      setJobs(res.data.jobs || []);
    } catch {
      toast.error("Erro ao carregar as vagas");
    } finally {
      setLoading(false);
    }
    try {
      const sig = await axios.get(`${API}/admin/hired-signals`);
      setSignals(sig.data.signals || []);
    } catch {
      setSignals([]);
    }
    try {
      const f = await axios.get(`${API}/admin/vagas-funil`);
      setFunil(f.data);
    } catch {
      setFunil(null);
    }
    try {
      const lo = await axios.get(`${API}/admin/mao-de-obra-divulgacao`);
      setLaborOffers(lo.data.offers || []);
    } catch {
      setLaborOffers([]);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const markFilled = async (job) => {
    if (!window.confirm(`A vaga "${job.title}" foi preenchida? Ela sai do ar e gera o post de comemoração para os grupos.`)) return;
    setFilling(job.id);
    try {
      const res = await axios.post(`${API}/admin/vagas/${job.id}/fill`);
      setMatch(res.data);
      toast.success("Vaga marcada como preenchida! 🎉");
      fetchJobs();
    } catch {
      toast.error("Erro ao marcar a vaga como preenchida");
    } finally {
      setFilling(null);
    }
  };

  const renewJob = async (job) => {
    setRenewing(job.id);
    try {
      await axios.post(`${API}/vaga/${job.id}/renew`);
      toast.success(`Vaga renovada por mais 15 dias! 🔄`);
      fetchJobs();
    } catch {
      toast.error("Erro ao renovar a vaga");
    } finally {
      setRenewing(null);
    }
  };

  const toggleUrgent = async (job) => {
    try {
      await axios.put(`${API}/admin/vagas/${job.id}/urgent`, { urgent: !job.urgent });
      toast.success(!job.urgent ? "Vaga marcada como 🔥 URGENTE — sobe para o topo em todo lugar!" : "Urgência removida.");
      fetchJobs();
    } catch {
      toast.error("Erro ao alterar a urgência");
    }
  };

  const confirmSignal = async (sig) => {
    try {
      const res = await axios.post(`${API}/admin/hired-signals/${sig.id}/confirm`);
      setMatch(res.data);
      toast.success("Contratação confirmada! 🎉 Post pronto para os grupos.");
      fetchJobs();
    } catch {
      toast.error("Erro ao confirmar a sinalização");
    }
  };

  const dismissSignal = async (sig) => {
    try {
      await axios.post(`${API}/admin/hired-signals/${sig.id}/dismiss`);
      toast.info("Sinalização ignorada.");
      fetchJobs();
    } catch {
      toast.error("Erro ao ignorar a sinalização");
    }
  };

  return (
    <div className="space-y-4" data-testid="job-broadcast-panel">
      <AdminBroadcastCard selectedJobs={selectedJobs} allJobs={jobs} selectedLabor={selectedLabor} allLabor={laborOffers} />
      {signals.length > 0 && (
        <Card className="border-amber-300 bg-amber-50" data-testid="hired-signals-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">🤝 Sinalizações de contratação ({signals.length})</CardTitle>
            <p className="text-xs text-amber-800">
              Pessoas avisaram que foram contratadas. Confirme para fechar a vaga e gerar o post de comemoração — ou ignore se for engano.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {signals.map((s) => (
              <div key={s.id} className="bg-white border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" data-testid={`hired-signal-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {s.user_first_name || "Alguém"} → <span className="text-gray-700">{s.job_title}</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    {s.job_city ? `📍 ${s.job_city} · ` : ""}
                    {s.allow_name ? "✅ autorizou citar o nome no post" : "🔒 sem nome no post"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 sm:shrink-0">
                  <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => confirmSignal(s)} data-testid={`signal-confirm-${s.id}`}>
                    ✅ Confirmar e gerar post
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => dismissSignal(s)} data-testid={`signal-dismiss-${s.id}`}>
                    Ignorar
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">📢 Divulgar Vagas nos Grupos</CardTitle>
          <p className="text-sm text-muted-foreground">
            Todas as vagas ativas num lugar só — da plataforma e as captadas dos grupos (Radar/Balcão).
            O anúncio gerado NÃO mostra o contato: quem se interessar se cadastra e informa as referências para ver.
          </p>
          {funil && (
            <div className="text-xs text-gray-600 bg-gray-50 border rounded-lg px-3 py-2 mt-1 flex flex-wrap gap-x-4 gap-y-1" data-testid="vagas-funil-stats">
              <span>👁 Contatos vistos: <b>{funil.contatos_vistos}</b>{funil.contatos_vistos_7d > 0 ? ` (${funil.contatos_vistos_7d} na semana)` : ""}</span>
              <span>🧾 Referências: <b>{funil.referencias}</b>{funil.referencias_7d > 0 ? ` (${funil.referencias_7d} na semana)` : ""}</span>
              <span>🤝 Sinalizações: <b>{funil.sinalizacoes}</b></span>
              <span>✅ Contratações: <b>{funil.contratacoes}</b></span>
              <span>🚪 Visitas ao Balcão (7d): <b>{funil.visitas_balcao_7d}</b></span>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Prévia/destaque do dia — recolhida até ser usada */}
          {!loading && !showPrevia && (
            <Button
              size="sm"
              variant="outline"
              className="mb-4 border-sky-300 text-sky-800 hover:bg-sky-50"
              onClick={() => setShowPrevia(true)}
              data-testid="broadcast-previa-toggle"
            >
              {previa.trim() ? "📰 Prévia do dia ✓ (editar)" : "➕ Adicionar prévia do dia"}
            </Button>
          )}
          {!loading && showPrevia && (
            <div className="mb-4 p-3 bg-sky-50 border border-sky-200 rounded-lg" data-testid="broadcast-previa-section">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-sky-900">📰 Prévia/destaque do dia (opcional)</p>
                <button onClick={() => setShowPrevia(false)} className="text-xs text-sky-700 underline shrink-0" data-testid="broadcast-previa-collapse">
                  ➖ Recolher
                </button>
              </div>
              <p className="text-xs text-sky-700 mt-0.5">
                Cole aqui a notícia do Radar ou um destaque (ex: "escassez pode elevar o preço da borracha") — o texto sai automaticamente no TOPO da mensagem escolhida abaixo.
              </p>
              <textarea
                value={previa}
                onChange={(e) => setPrevia(e.target.value)}
                className="mt-2 w-full text-xs border rounded-lg p-3 bg-white h-20"
                placeholder="Cole aqui a prévia/notícia do dia..."
                data-testid="broadcast-previa-text"
              />
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="text-xs text-sky-800 font-medium">Sai junto com:</span>
                {[
                  { v: "vagas", l: "💼 Vagas" },
                  { v: "profissionais", l: "🤝 Profissionais" },
                  { v: "ambos", l: "✅ Ambos" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setPreviaScope(opt.v)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${previaScope === opt.v ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-800 border-sky-300 hover:bg-sky-100"}`}
                    data-testid={`previa-scope-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                ))}
                {previa.trim() && (
                  <Button size="sm" variant="ghost" onClick={() => setPrevia("")} data-testid="broadcast-previa-clear">
                    🧹 Limpar
                  </Button>
                )}
              </div>
            </div>
          )}
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando vagas...</p>
          ) : jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma vaga ativa no momento.</p>
          ) : (
            <>
              {/* Divulgação em lote — todas, selecionadas ou só as confirmadas */}
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="broadcast-all-section">
                <p className="text-sm font-semibold text-green-900">📦 Divulgar vagas ({jobsToShare.length} na mensagem)</p>
                <p className="text-xs text-green-700 mt-0.5">
                  Sem marcar nada, vão todas. Marque ☑️ nas vagas abaixo para escolher só algumas.
                </p>
                <label className="flex items-start gap-2 text-xs text-green-900 mt-2 cursor-pointer" data-testid="broadcast-only-confirmed">
                  <input
                    type="checkbox"
                    checked={onlyConfirmed}
                    onChange={(e) => setOnlyConfirmed(e.target.checked)}
                    className="mt-0.5 accent-green-700"
                  />
                  <span>Só as com selo <strong>✅ confirmadas ({confirmedCount})</strong> — quem respondeu "ainda está disponível" pelo 📲 WhatsApp</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-green-900 mt-1.5 cursor-pointer" data-testid="broadcast-include-note">
                  <input
                    type="checkbox"
                    checked={includeNote}
                    onChange={(e) => setIncludeNote(e.target.checked)}
                    className="mt-0.5 accent-green-700"
                  />
                  <span>Incluir a <strong>nota de esclarecimento</strong> no início da mensagem ("corrigimos o acesso às vagas")</span>
                </label>
                {onlyConfirmed && jobsToShare.length === 0 && (
                  <p className="text-xs text-amber-700 mt-2" data-testid="broadcast-no-confirmed-hint">
                    Nenhuma vaga confirmada ainda — use o 📲 Confirmar nos anúncios (mais abaixo) para pedir a confirmação no WhatsApp.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2 sm:flex sm:flex-wrap">
                  <Button size="sm" onClick={copyDigest} disabled={jobsToShare.length === 0} data-testid="broadcast-copy-all-btn">
                    📋 Copiar ({jobsToShare.length})
                  </Button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(digestText)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="broadcast-whatsapp-all-btn"
                    className="contents sm:inline-block"
                  >
                    <Button size="sm" className="w-full sm:w-auto bg-green-600 hover:bg-green-700" disabled={jobsToShare.length === 0}>💬 WhatsApp</Button>
                  </a>
                  <Button size="sm" variant="outline" className="col-span-2 sm:col-span-1" onClick={() => setShowDigest(!showDigest)} data-testid="broadcast-preview-all-btn">
                    {showDigest ? "Ocultar prévia" : "👁 Ver prévia"}
                  </Button>
                  {selectedJobs.length > 0 && (
                    <Button size="sm" variant="ghost" className="col-span-2 sm:col-span-1" onClick={() => setSelected({})} data-testid="broadcast-clear-selected-btn">
                      Limpar seleção ({selectedJobs.length})
                    </Button>
                  )}
                </div>
                {showDigest && (
                  <textarea
                    readOnly
                    value={digestText}
                    className="mt-2 w-full text-xs border rounded-lg p-3 bg-white h-56"
                    data-testid="broadcast-digest-text"
                  />
                )}
              </div>

              <p className="text-xs text-gray-500 mb-2">Ou divulgue uma vaga individual:</p>
              <div className="space-y-2">
              {jobs.map((j) => (
                <div
                  key={j.id}
                  className={`border rounded-lg p-3 ${j.urgent ? "border-red-300 bg-red-50/40" : ""}`}
                  data-testid={`broadcast-job-${j.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                  <div className="min-w-0 flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={!!selected[j.id]}
                      onChange={() => toggleSelect(j.id)}
                      className="mt-1 w-4 h-4 accent-amber-600 shrink-0 cursor-pointer"
                      title="Marcar como confirmada para divulgar"
                      data-testid={`broadcast-select-${j.id}`}
                    />
                    <div className="min-w-0">
                    <div className="text-sm font-medium flex flex-wrap items-center gap-1">
                      {j.urgent && <Badge className="bg-red-100 text-red-700 text-[10px]" data-testid={`urgent-badge-${j.id}`}>🔥 URGENTE</Badge>}
                      {j.kind === "plataforma" ? (
                        <Badge className="bg-green-100 text-green-800 text-[10px]">PLATAFORMA</Badge>
                      ) : (
                        <Badge className="bg-sky-100 text-sky-800 text-[10px]">DOS GRUPOS</Badge>
                      )}
                      {j.is_example && <Badge variant="outline" className="text-[10px]">exemplo</Badge>}
                      {j.title}
                    </div>
                    <p className="text-xs text-gray-600">
                      {j.location ? `📍 ${j.location}` : ""}
                      {j.salary_range ? ` · 💰 ${j.salary_range}` : ""}
                      {j.submitter_name ? ` · indicada por ${j.submitter_name}` : ""}
                      {j.days_left != null ? (
                        <span className={j.days_left <= 3 ? " text-amber-700 font-semibold" : ""}>
                          {` · ⏰ vence em ${j.days_left}d`}
                        </span>
                      ) : ""}
                    </p>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:shrink-0">
                    <Button
                      size="sm"
                      className="flex-1 sm:flex-none bg-sky-600 hover:bg-sky-700"
                      onClick={() => setShareJob(j)}
                      data-testid={`broadcast-share-btn-${j.id}`}
                    >
                      📢 Divulgar
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={`broadcast-more-btn-${j.id}`}>⋯ Mais</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => toggleExpand(j.id)} data-testid={`broadcast-view-btn-${j.id}`}>
                          {expanded[j.id] ? "👁 Ocultar detalhes" : "👁 Ver detalhes"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleUrgent(j)} data-testid={`broadcast-urgent-btn-${j.id}`}>
                          {j.urgent ? "🔥 Tirar urgência" : "🔥 Marcar urgente"}
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={renewing === j.id} onClick={() => renewJob(j)} data-testid={`broadcast-renew-btn-${j.id}`}>
                          🔄 Renovar +15 dias
                        </DropdownMenuItem>
                        <DropdownMenuItem disabled={filling === j.id} onClick={() => markFilled(j)} data-testid={`broadcast-filled-btn-${j.id}`}>
                          ✅ Marcar preenchida
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  </div>
                  {expanded[j.id] && (
                    <div className="mt-2 sm:ml-6 text-xs bg-gray-50 border rounded-lg p-3 space-y-1.5" data-testid={`broadcast-job-full-${j.id}`}>
                      {j.description ? (
                        <p className="whitespace-pre-wrap text-gray-800">{j.description}</p>
                      ) : (
                        <p className="text-gray-500 italic">Esta vaga não tem descrição detalhada.</p>
                      )}
                      {j.requirements && <p className="text-gray-700"><b>Requisitos:</b> {j.requirements}</p>}
                      {j.job_type && <p className="text-gray-700"><b>Tipo:</b> {j.job_type}</p>}
                      {j.expected_start_date && <p className="text-gray-700"><b>Início previsto:</b> {j.expected_start_date.slice(0, 10).split("-").reverse().join("/")}</p>}
                      {j.employer_name && <p className="text-gray-700"><b>Anunciante:</b> {j.employer_name}</p>}
                      {j.contact && <p className="text-gray-700"><b>📱 Contato (visível só para você):</b> {j.contact}</p>}
                      {j.source_name && <p className="text-gray-700"><b>Fonte:</b> {j.source_name}</p>}
                      {j.source_url && (
                        <a href={j.source_url} target="_blank" rel="noreferrer" className="text-sky-700 underline" data-testid={`broadcast-job-source-${j.id}`}>
                          🔗 Abrir anúncio original
                        </a>
                      )}
                      <p className="text-gray-400 pt-1">O anúncio divulgado nos grupos NÃO inclui o contato — ele só aparece aqui para você identificar a vaga.</p>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      {/* Divulgar Profissionais Disponíveis (mão de obra) */}
      <Card data-testid="labor-broadcast-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">🤝 Divulgar Profissionais Disponíveis</CardTitle>
          <p className="text-sm text-muted-foreground">
            Ofertas de mão de obra do Balcão e dos profissionais da plataforma. O anúncio NÃO mostra o telefone — direciona para o Balcão, onde o contato aparece.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="w-fit mt-1 border-amber-400 text-amber-800 hover:bg-amber-50"
            onClick={notifyMissingRefs}
            data-testid="notify-missing-refs-btn"
          >
            📝 Avisar profissionais sem referências (sininho)
          </Button>
        </CardHeader>
        <CardContent>
          {laborOffers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma oferta de mão de obra ativa no momento.</p>
          ) : (
            <>
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  Marque ☑️ quem você quer divulgar (sem marcar nenhum, vão <strong>todos os {laborOffers.length}</strong>).
                </p>
                <div className="grid grid-cols-2 gap-2 mt-2 sm:flex sm:flex-wrap">
                  <Button size="sm" onClick={copyLabor} data-testid="labor-copy-btn">
                    📋 Copiar ({laborToShare.length})
                  </Button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(laborText)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="labor-whatsapp-btn"
                    className="contents sm:inline-block"
                  >
                    <Button size="sm" className="w-full sm:w-auto bg-green-600 hover:bg-green-700">💬 WhatsApp</Button>
                  </a>
                  <Button size="sm" variant="outline" className="col-span-2 sm:col-span-1" onClick={() => setShowLaborPreview(!showLaborPreview)} data-testid="labor-preview-btn">
                    {showLaborPreview ? "Ocultar prévia" : "👁 Ver prévia"}
                  </Button>
                  {selectedLabor.length > 0 && (
                    <Button size="sm" variant="ghost" className="col-span-2 sm:col-span-1" onClick={() => setLaborSelected({})} data-testid="labor-clear-btn">
                      Limpar seleção
                    </Button>
                  )}
                </div>
                {showLaborPreview && (
                  <textarea
                    readOnly
                    value={laborText}
                    className="mt-2 w-full text-xs border rounded-lg p-3 bg-white h-48"
                    data-testid="labor-preview-text"
                  />
                )}
              </div>
              <div className="space-y-2">
                {laborOffers.map((o) => (
                  <div key={o.id} className="border rounded-lg p-3 flex items-start gap-2" data-testid={`labor-offer-row-${o.id}`}>
                    <input
                      type="checkbox"
                      checked={!!laborSelected[o.id]}
                      onChange={() => toggleLaborSelect(o.id)}
                      className="mt-1 w-4 h-4 accent-amber-600 shrink-0 cursor-pointer"
                      data-testid={`labor-select-${o.id}`}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium flex flex-wrap items-center gap-1">
                        {o.source === "plataforma" ? (
                          <Badge className="bg-green-100 text-green-800 text-[10px]">PLATAFORMA</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 text-[10px]">BALCÃO</Badge>
                        )}
                        {o.title}
                      </div>
                      <p className="text-xs text-gray-600">
                        {[o.city ? `📍 ${o.city}` : "", o.name || "", o.contact ? `📱 ${o.contact} (não sai no anúncio)` : ""].filter(Boolean).join(" · ")}
                      </p>
                      {o.summary && <p className="text-xs text-gray-500 mt-0.5">{o.summary}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <BalcaoManageCards />

      <ShareJobDialog job={shareJob} open={!!shareJob} onOpenChange={(o) => !o && setShareJob(null)} />
      <MatchDialog match={match} open={!!match} onOpenChange={(o) => !o && setMatch(null)} />
    </div>
  );
};

export default JobBroadcastPanel;
